// Types
import { IContact } from '@trii/types/dist/Contacts';
import { GeneralProps } from '../General';
import { UserInfo } from '@trii/types/dist/Users';
// Hooks
import { useEffect, useState } from 'react';
import { useEditEntity } from '../../../../context/EditEntityContext';
import { useEditContactApi } from '../../../../context/EditContactApiContext';
// Api
// Utils
import { resolveUserLabel } from '../../utils/resolveUserLabel';
// Layout
import { Section, SectionContent } from '../../../../layout';
// UI
import { ContactInfoLabel, EntityCustomFieldLabel } from '../../../../shared';
import { useTranslationContext } from '../../../../context/TranslationContext';

export type ContactGeneralProps = Omit<GeneralProps, 'entityData' | 'entityType'> & {
  entityType: 'contact';
  entityData: IContact;
};

const ContactGeneral = (props: ContactGeneralProps) => {
  const {
    dateFormat,
    entityData,
    customContactFields,
    baseUrl,
    spaceId,
    currentUser,
    onEntityUpdated,
  } = props;

  const t = useTranslationContext();
  const { entityType, editEntity } = useEditEntity();
  const api = useEditContactApi();

  if (entityType !== 'contact') {
    return null;
  }

  const [users, setUsers] = useState<UserInfo[]>([]);
  const [owner, setOwner] = useState<string | null>(null);
  const [updatedBy, setUpdatedBy] = useState<string | null>(null);
  const [createdBy, setCreatedBy] = useState<string | null>(null);

  const editContact = editEntity;

  useEffect(() => {
    if (!api) return;
    const controller = new AbortController();

    api
      .fetchUsers({ signal: controller.signal })
      .then((data) => {
        if (controller.signal.aborted) return;
        setUsers(data ?? []);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setUsers([]);
      });

    return () => {
      controller.abort();
    };
  }, [api]);

  useEffect(() => {
    const controller = new AbortController();

    const getRemoteLabel = async (userId?: string) => {
      if (!userId) return null;
      if (!api) return null;
      try {
        const userTrii = await api.fetchUserTriiById({ userId, signal: controller.signal });
        return userTrii?.display_name || userTrii?.email || null;
      } catch {
        return null;
      }
    };

    const resolve = async () => {
      const ownerLabel =
        resolveUserLabel({ userId: entityData.owner, users, currentUser }) ??
        (entityData.owner ? await getRemoteLabel(entityData.owner) : null);

      const createdByLabel =
        resolveUserLabel({ userId: entityData.createdBy ?? undefined, users, currentUser }) ??
        (entityData.createdBy ? await getRemoteLabel(entityData.createdBy) : null);

      const updatedByLabel =
        resolveUserLabel({ userId: entityData.updatedBy ?? undefined, users, currentUser }) ??
        (entityData.updatedBy ? await getRemoteLabel(entityData.updatedBy) : null);

      if (controller.signal.aborted) return;

      setOwner(ownerLabel);
      setCreatedBy(createdByLabel);
      setUpdatedBy(updatedByLabel);
    };

    resolve();
    return () => {
      controller.abort();
    };
  }, [api, entityData, users, currentUser]);

  const userDateFormat = (dateFormat || 'YYYY/MM/DD').toUpperCase();
  const birthDate = editContact.field.birthDate;
  const dateOnlyString = birthDate?.isValid() ? birthDate.format(userDateFormat) : null;

  const calculateAge = (birthDate: Date) => {
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();

    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  };

  return (
    <Section title="General">
      <SectionContent>
        {/* CONTACT LABELS */}
        <ContactInfoLabel
          title={t('conversations.contactDetails.contactOwner')}
          displayValue={owner || '-'}
          value={entityData.owner}
          inputType="select"
          options={users}
          constructUpdateObject={(value) => ({
            owner: typeof value === 'string' ? value : '',
          })}
          contact={editContact}
          baseUrl={baseUrl}
          spaceId={spaceId}
          contactId={entityData.id}
          onEntityUpdated={onEntityUpdated}
        />
        <ContactInfoLabel
          title={t('conversations.contactDetails.business')}
          displayValue={entityData.businessName || '-'}
          value={entityData.businessId}
          inputType="selectBusiness"
          businessId={entityData.businessId}
          contact={editContact}
          baseUrl={baseUrl}
          spaceId={spaceId}
          contactId={entityData.id}
          onEntityUpdated={onEntityUpdated}
        />
        <ContactInfoLabel
          title={t('conversations.contactDetails.createdBy')}
          value={createdBy || '-'}
          isNotEditable={true}
          contact={editContact}
          baseUrl={baseUrl}
          spaceId={spaceId}
          contactId={entityData.id}
        />
        <ContactInfoLabel
          title={t('conversations.contactDetails.modifiedBy')}
          value={updatedBy || '-'}
          isNotEditable={true}
          contact={editContact}
          baseUrl={baseUrl}
          spaceId={spaceId}
          contactId={entityData.id}
        />
        <ContactInfoLabel
          title={t('global.birthdate')}
          inputType="birthday"
          displayValue={birthDate ? `${dateOnlyString} (${calculateAge(birthDate.toDate())})` : '-'}
          value={birthDate ? birthDate.toISOString() : ''}
          constructUpdateObject={(value) => ({
            birthDate: value,
          })}
          contact={editContact}
          baseUrl={baseUrl}
          spaceId={spaceId}
          contactId={entityData.id}
          dateFormat={dateFormat}
          onEntityUpdated={onEntityUpdated}
        />

        {editContact.field.customProperties.map((property) => (
          <EntityCustomFieldLabel
            key={property.nameKey}
            property={property}
            entity={editContact}
            entityType="contact"
            entityId={String(entityData.id)}
            customContactFields={customContactFields}
            userTrii={currentUser}
            baseUrl={baseUrl}
            spaceId={spaceId}
            onEntityUpdated={onEntityUpdated}
          />
        ))}
        {/*END OF CONTACT LABELS */}
      </SectionContent>
    </Section>
  );
};

export default ContactGeneral;
